import { buildImageSrc } from '@bridebook/toolbox/src/buildImageUrl';
import { isPrimaryPhotoSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { CSSProperties } from 'react';
import { useWeddingWebsiteData } from '../../data/data-context';
import { getPhotoOrientation } from '../../sections/photo-orientation';
import { MotifOrnament, MotifSectionHookProps } from './motif-section-hooks';

/**
 * Standalone website photo, interleaved between content sections by arrangeSections.
 * Emits the per-photo orientation (clamped) as a modifier class + inline aspect-ratio so the external theme CSS can
 * frame portrait/landscape photos correctly without knowing the data. See layout-motif/docs/bem-contract.md.
 */
export const MotifPhoto = ({ index, ...hookProps }: { index: number } & MotifSectionHookProps) => {
  const { website } = useWeddingWebsiteData();
  const photo = website.sections.find(isPrimaryPhotoSection)?.data[index];
  if (!photo?.path) return null;

  const { orientation, aspectRatioString, desktopWidth, desktopHeight, mobileWidth } =
    getPhotoOrientation({
      width: photo.width,
      height: photo.height,
    });

  const crop = { fit: 'cover', gravity: 'face' } as const;
  const src = buildImageSrc(photo.path, { w: desktopWidth, h: desktopHeight, ...crop });

  // 1x and 2x of both layout widths; h follows the same clamped ratio so the face crop is identical
  // across variants. `sizes` splits on the primitives' shared 640px container breakpoint.
  const ratio = desktopWidth / desktopHeight;
  const widths = [...new Set([mobileWidth, desktopWidth, mobileWidth * 2, desktopWidth * 2])];
  const srcSet = widths
    .map((w) => `${buildImageSrc(photo.path, { w, h: Math.round(w / ratio), ...crop })} ${w}w`)
    .join(', ');
  const sizes = `(max-width: 640px) 100vw, ${desktopWidth}px`;

  return (
    <section {...hookProps} className="bb-ww__section bb-ww__section--photo">
      <MotifOrnament />
      <figure
        className={`bb-ww__image-card bb-ww__image-card--${orientation}`}
        style={{ aspectRatio: aspectRatioString } as CSSProperties}>
        <img
          className="bb-ww__image"
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt=""
          loading="lazy"
        />
      </figure>
    </section>
  );
};
